import React, {useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import {Formik} from 'formik';
import {isEqual} from 'lodash';
import {useDispatch} from 'react-redux';
import moment from 'moment';
import {useUsersClientActionsContext} from '../context/UsersClientActionsContext';
import FilterTextField from '../../_common/components/FilterTextField';
import FilterSelect from '../../_common/components/FilterSelect';
import {FilterFromDate, FilterToDate} from '../../_common/components/FilterDateRange';
import FiltersBar from '../../_common/components/FiltersBar';
import ButtonExportToExcel from '../../_common/components/ButtonExportToExcel';
import * as actions from '../_redux/usersClientActionsActions';
import {RequestCancellationManager} from '../../_common/utils/RequestCancellationManager';
import {generateExcelFile} from '../../_common/utils/excel';
import {usersClientActionsHeader} from '../../_common/constants/exportedFilesMainHeaders';
import {headersUsersClientActions} from '../../_common/constants/exportedFilesHeaders';
import {actionTypesUsersClient as T} from "../../_common/constants/actionTypes";
import {results as R} from "../../_common/constants/results";

const prepareFilter = (queryParams, values) => {
  const {
    username,
    userEmail,
    fromDate,
    toDate,
    action,
    result
  } = values;
  const newQueryParams = {...queryParams};
  const filter = {};
  if (Boolean(username)) {
    filter.username = username;
  } else {
    delete newQueryParams.username;
  }
  if (Boolean(userEmail)) {
    filter.userEmail = userEmail;
  } else {
    delete newQueryParams.userEmail;
  }
  if (Boolean(fromDate)) {
    filter.fromDate = moment(fromDate).format('YYYY-MM-DD');
  } else {
    delete newQueryParams.fromDate;
  }
  if (Boolean(toDate)) {
    filter.toDate = moment(toDate).add(1, 'days').format('YYYY-MM-DD');
  } else {
    delete newQueryParams.toDate;
  }
  if (Boolean(action)) {
    filter.action = action;
  } else {
    delete newQueryParams.action;
  }
  if (Boolean(result)) {
    filter.result = result;
  } else {
    delete newQueryParams.result;
  }

  return {...newQueryParams, ...filter};
};

export function UsersClientActionsFilter() {
  const dispatch = useDispatch();
  const intl = useIntl();
  const context = useUsersClientActionsContext();
  const props = useMemo(() => {
    return {
      queryParams: context.queryParams,
      setQueryParams: context.setQueryParams,
    };
  }, [context]);

  const applyFilter = (values) => {
    RequestCancellationManager.stopRequests(actions.getUsersClientActionsListIDPrefix);

    const newQueryParams = prepareFilter(props.queryParams, values);
    if (!isEqual(newQueryParams, props.queryParams)) {
      props.setQueryParams(newQueryParams);
    }
  };

  const qs = new URLSearchParams(window.location.search);
  let username = qs.get('username') || '';
  let userEmail = qs.get('userEmail') || '';
  const fromDate = qs.get('fromDate') || '';
  const toDate = Boolean(qs.get('toDate'))
    ? moment(qs.get('toDate')).subtract(1, 'days').toDate()
    : '';
  let action = qs.get('action') || '';
  let result = qs.get('result') || '';

  const [loading, setLoading] = useState(false);
  const handleExport = () => {
    setLoading(true);
    dispatch(actions.getUsersClientActivitiesLogsForExcelFile(props.queryParams))
      .then(prepareDataForExcel)
      .then((data) => generateExcelFile(data, 'Users_client_actions.xlsx', usersClientActionsHeader))
      .catch((error) => {
        console.error(`Error: + ${error}`);
        setLoading(false);
      })
      .finally(() => setLoading(false));
  };
  const prepareDataForExcel = data => {
    return data.map((item, index) => {
      const {
        username,
        userEmail,
        createdAt,
        page,
        action,
        payload,
        subject,
        result,
      } = item;

      return {
        [headersUsersClientActions[0]]: index + 1,
        [headersUsersClientActions[1]]: [username, userEmail].filter(Boolean).join(' - '),
        [headersUsersClientActions[2]]: Boolean(createdAt) ? moment(createdAt).format('DD.MM.YYYY HH:mm:ss') : '',
        [headersUsersClientActions[3]]: action,
        [headersUsersClientActions[4]]: page,
        [headersUsersClientActions[4]]: subject,
        [headersUsersClientActions[5]]: payload,
        [headersUsersClientActions[6]]: result,
      };
    });
  };

  const containerClassnames = 'w-200px position-relative my-1 px-3';
  const actionTypeOptions = [
    {label: T.GENERAL, value: T.GENERAL},
    {label: T.PAYMENTS, value: T.PAYMENTS},
    {label: T.INVOICES, value: T.INVOICES},
  ];
  const actionResultsOptions = [
    {label: R.SUCCESS, value: R.SUCCESS},
    {label: R.FAIL, value: R.FAIL},
  ];

  return (
    <>
      <Formik
        initialValues={{
          username,
          userEmail,
          fromDate,
          toDate,
          action,
          result,
        }}
        onSubmit={(values) => applyFilter(values)}
        enableReinitialize={true}
      >
        {({values, ...formikProps}) => (
          <FiltersBar
            filters={
              <div className={'d-flex flex-wrap justify-content-center align-items-center mb-0 w-100'}>
                <FilterTextField
                  fieldName={'username'}
                  placeholder={intl.formatMessage({id: 'users-actions-filter-username', defaultMessage: 'Username'})}
                  value={values.username}
                  containerStyles={containerClassnames}
                  {...formikProps}
                />
                <FilterTextField
                  fieldName={'userEmail'}
                  placeholder={intl.formatMessage({id: 'users-actions-filter-email', defaultMessage: 'User email'})}
                  value={values.userEmail}
                  containerStyles={containerClassnames}
                  {...formikProps}
                />
                <FilterFromDate
                  values={values}
                  containerStyles={containerClassnames}
                  {...formikProps}
                />
                <FilterToDate
                  values={values}
                  containerStyles={containerClassnames}
                  {...formikProps}
                />
                <FilterSelect
                  fieldName={'action'}
                  valueName={values.action}
                  options={actionTypeOptions}
                  placeholder={intl.formatMessage({id: 'users-actions-filter-action', defaultMessage: 'Action'})}
                  containerStyles={containerClassnames}
                  {...formikProps}
                />
                <FilterSelect
                  fieldName={'result'}
                  valueName={values.result}
                  options={actionResultsOptions}
                  placeholder={intl.formatMessage({id: 'users-actions-filter-result', defaultMessage: 'Result'})}
                  containerStyles={containerClassnames}
                  {...formikProps}
                />
              </div>
            }
            buttons={
              <ButtonExportToExcel
                loading={loading}
                exportFunc={handleExport}
              />
            }
          />
        )}
      </Formik>
    </>
  );
}
